import { IL_LOSS_BASE } from '@/constants/earn';
import { QUERY_KEYS } from '@/constants/query';
import { useChainId } from '@/hooks/web3/useChain';
import { ILDataParams } from '@/types/earn';
import { SimulateImpermenantLossResult } from '@synfutures/sdks-perp';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useSDK } from '../web3/hook';

export function useILData(params: ILDataParams | undefined): UseQueryResult<SimulateImpermenantLossResult[]> {
  const chainId = useChainId();
  const sdk = useSDK(chainId);
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: QUERY_KEYS.EARN.IL(
      chainId,
      params?.symbol,
      params?.alphaWadLower.toString(),
      params?.alphaWadUpper.toString(),
    ),
    queryFn: async () => {
      try {
        if (params) {
          const data = await sdk?.perp.simulate.simulateImpermanentLoss(params);
          return (
            data?.map((d) => ({
              tick: d.tick * (params.isInverse ? -1 : 1),
              impermanentLoss: d.impermanentLoss * -1 + IL_LOSS_BASE,
            })) || []
          );
        }
        return [];
      } catch (e) {
        console.log('🚀 ~ queryFn: ~ e:', e);
        return [];
      }
    },
    enabled: !!sdk && !!params,
  });
}

/**
 * Fetch whitelist config for Earn (allowUnauthorizedLPs and isInWhiteList) using React Query.
 * This refactors the previous Redux thunk fetchEarnWhitelistConfig into query-based fetching.
 */
export function useWhitelistConfig(params: {
  chainId?: number;
  userAddr?: string;
  quoteTokenAddress?: string;
}): UseQueryResult<{ allowUnauthorizedLPs: boolean | undefined; isInWhiteList: boolean }, unknown> {
  const sdk = useSDK(params.chainId);
  return useQuery({
    // Include all inputs referenced by queryFn to satisfy exhaustive-deps for query keys
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      ...QUERY_KEYS.EARN.WHITELIST(params.chainId, params.userAddr, params.quoteTokenAddress),
      !!sdk,
      !!sdk?.perp?.config,
    ],
    queryFn: async () => {
      if (!sdk || !params.chainId || !params.userAddr || !params.quoteTokenAddress)
        return { allowUnauthorizedLPs: undefined, isInWhiteList: false };
      const allowUnauthorizedLPs = await sdk.perp.config.openLp(params.quoteTokenAddress);
      const isInWhiteListArr = await sdk.perp.config.inWhiteListLps(params.quoteTokenAddress, [params.userAddr]);
      return {
        allowUnauthorizedLPs,
        isInWhiteList: Array.isArray(isInWhiteListArr) && isInWhiteListArr.length > 0 ? !!isInWhiteListArr[0] : false,
      };
    },
    enabled: !!sdk && !!sdk?.perp?.config && !!params.chainId && !!params.userAddr && !!params.quoteTokenAddress,
  });
}
